import { Box, Button, Divider, Grid, TextField, Typography } from "@mui/material";
import React from "react";
import {
  ICommonFormikProps,
  ICreateNewAudience,
} from "../CreateAudienceRightPanel.types";
import DownloadIcon from "@mui/icons-material/Download";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { SingleFileDisplayV2 } from "../../../../../components/FileUpload/SingleFileDisplayV2";
import { PreviewExcelFile } from "../../../../../components/Preview/PreviewExcelFile";
import { ExcelFileUplaodButtonAsync } from "../../../../marketingCampaign/components/ExcelFileUplaodButtonAsync";
import { PreviewCSVFile } from "../../../../../components/Preview/PreviewCSVFile";
import { getFileExtension } from "../../../../../components/FileUploadIcon/helpers";
import { config } from "../../../../../config";

const IMAGE_BASE = config.assets.s3BaseUrl?.replace(/\/$/, "") ?? "";
const AUDIENCE_SAMPLE_FILE_URL = `${IMAGE_BASE}/sample/SampleCampaign.xlsx`;
const AUDIENCE_SAMPLE_CSV_URL = `${IMAGE_BASE}/sample/SampleCampaign.csv`;
const AUDIENCE_SAMPLE_IMAGE_URL = `${IMAGE_BASE}/sample/SampleImage.png`;

type IStep1UploadAndDetails = ICommonFormikProps<ICreateNewAudience>;

export const Step1UploadAndDetails: React.FC<IStep1UploadAndDetails> = ({
  values,
  handleChange,
  setFieldValue,
  setValues,
  errors,
}) => {
  const handleAudienceNameBlur = () => {
    const normalized = (values.audience_name ?? "")
      .replace(/\s+/g, " ")
      .trim();

    if (normalized !== values.audience_name) {
      setFieldValue("audience_name", normalized);
    }
  };

  const handleAudienceFileUploadSuccess = (
    path: string,
    extractedHeaders: string[],
    data: Record<string, any>[],
    file?: File
  ) => {
    setValues((prev) => ({
      ...prev,
      source_link: path,
      headers: extractedHeaders,
      sample_data: data,
      localFile: file || null,
    }));
  };

  return (
    <Grid container spacing={3}>
      <>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Audience Name"
            name="audience_name"
            value={values.audience_name}
            onChange={handleChange}
            onBlur={handleAudienceNameBlur}
            required
            error={!!errors.audience_name}
            helperText={errors.audience_name as string}
          />
        </Grid>
      </>

      <Grid item xs={4}>
        <Grid container>
          <Grid item xs={12} sx={{ my: 2, ml: -1.5 }}>
            <Divider sx={{ rotate: "90deg", width: "50px" }} />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography
                sx={{
                  minWidth: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                1
              </Typography>

              <Box>
                <Typography variant="h3" fontWeight={600}>
                  Upload Your Data File
                </Typography>
                <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                  Fill in your data using the same format and upload your
                  completed file
                </Typography>

                {values.source_link.length > 0 ? (
                  <>
                    {/* <SingleFileDisplay
                      value={values.source_link}
                      onDelete={() => {
                        setValues((prev) => ({
                          ...prev,
                          source_link: "",
                          headers: [],
                          mappings: [],
                          sample_data: [],
                          isMappingCompleted: false,
                        }));
                      }}
                    /> */}

                    <SingleFileDisplayV2
                      file={values.localFile}
                      value={values.source_link}
                      onDelete={() => {
                        setValues((prev) => ({
                          ...prev,
                          source_link: "",
                          headers: [],
                          mappings: [],
                          sample_data: [],
                          isMappingCompleted: false,
                          localFile: null,
                        }));
                      }}
                    />
                    {values.isMappingCompleted ? (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          backgroundColor: "#f0fdf4",
                          borderRadius: 1,
                          border: "1px solid #bbf7d0",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="success.dark"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          ✓ Field mapping completed successfully
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {values.mappings.length} fields mapped
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          backgroundColor: "#fef3c7",
                          borderRadius: 1,
                          border: "1px solid #fbbf24",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="warning.dark"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          ⚠️ Field mapping required
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Please complete the field mapping before saving
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        startIcon={<DownloadIcon />}
                        component="a"
                        href={AUDIENCE_SAMPLE_FILE_URL}
                        download="SampleCampaign.xlsx"
                      >
                        Download Sample XLSX
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        startIcon={<DownloadIcon />}
                        component="a"
                        href={AUDIENCE_SAMPLE_CSV_URL}
                        download="SampleCampaign.csv"
                      >
                        Download Sample CSV
                      </Button>
                    </Box>

                    <ExcelFileUplaodButtonAsync
                      onUploadSuccess={handleAudienceFileUploadSuccess}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ my: 2, ml: -1.5 }}>
            <Divider sx={{ rotate: "90deg", width: "50px" }} />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "2px solid",
                borderColor: "warning.main",
                backgroundColor: "#fff8e1",
                boxShadow: "0 2px 8px rgba(251, 191, 36, 0.15)",
              }}
            >
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 1.5 }}>
                <TipsAndUpdatesIcon sx={{ fontSize: 28, color: "warning.dark" }} />
                <Typography
                  variant="h3"
                  fontWeight={700}
                  sx={{ color: "warning.dark", textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  Important — Read Before Uploading
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  borderRadius: 1,
                  backgroundColor: "error.main",
                  color: "error.contrastText",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <PriorityHighIcon sx={{ fontSize: 22 }} />
                <Typography variant="body2" fontWeight={700}>
                  <Box
                    component="code"
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      mx: 0.5,
                      borderRadius: 0.5,
                      backgroundColor: "rgba(255,255,255,0.25)",
                      fontFamily: "monospace",
                      fontWeight: 700,
                    }}
                  >
                    phone
                  </Box>
                  column is REQUIRED — do not rename or remove it
                </Typography>
              </Box>

              <Typography
                variant="body1"
                component="ul"
                sx={{
                  pl: 2.5,
                  m: 0,
                  "& li": { mb: 0.75, fontWeight: 500 },
                  "& li:last-child": { mb: 0 },
                }}
              >
                <li>First row must be column headers</li>
                <li>
                  You can add any number of additional columns after these
                  (e.g., email, city, age) — name them whatever you want
                </li>
                <li>Each row after the header is one customer record</li>
                <li>No empty rows between entries</li>
                <li>
                  Save your file as <strong>CSV</strong> or{" "}
                  <strong>Excel (.xlsx)</strong> format
                </li>
                <li>Remove any extra formatting or special characters</li>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {values.source_link ? (
        <Grid item xs={8}>
          {["xlsx", "xls"].includes(getFileExtension(values.source_link)) ? (
            <PreviewExcelFile filePath={values.source_link} />
          ) : getFileExtension(values.source_link) === "csv" ? (
            <PreviewCSVFile filePath={values.source_link} />
          ) : (
            <></>
          )}
        </Grid>
      ) : (
        <Grid item xs={8}>
          <Box
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              backgroundColor: "background.default",
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Sample file preview
            </Typography>
            <Typography variant="caption" color="text.secondary" mb={1.5} display="block">
              Your file should look like this
            </Typography>
            <Box
              component="img"
              src={AUDIENCE_SAMPLE_IMAGE_URL}
              alt="Sample CSV layout"
              sx={{
                width: "100%",
                maxHeight: 420,
                minHeight: 200,
                objectFit: "contain",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                display: "block",
                backgroundColor: "#fff",
              }}
            />
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
